import { ContainerSpec } from '@/types/schema';

import { TextField } from '../TextField';

interface ContainerSpecEditorProps {
  value: ContainerSpec | undefined;
  onChange: (spec: ContainerSpec | undefined) => void;
}

const parseNumberValue = (value: string): number | undefined => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
};

export function ContainerSpecEditor({
  value,
  onChange,
}: ContainerSpecEditorProps) {
  const handleFieldChange =
    (field: keyof ContainerSpec) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...value,
        [field]: parseNumberValue(e.target.value),
      });
    };

  return (
    <>
      <div className="flex flex-col items-start">
        <strong className="font-semibold">Container Resources</strong>
        <p className="text-sm text-tertiary">
          Specify CPU and memory requirements for each container and the mininum
          and maximum number of containers (scale) for your deployment.
        </p>
        <br />
        <p className="text-sm text-tertiary">
          Default values will be used if not specified.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="CPU per container"
            type="number"
            onChange={handleFieldChange('cpu')}
            value={value?.cpu ?? ''}
            placeholder="1"
          />
          <TextField
            label="Memory (MB) per container"
            type="number"
            onChange={handleFieldChange('memory_mb')}
            value={value?.memory_mb ?? ''}
            placeholder="1024"
          />
          <TextField
            label="Minimum scale"
            type="number"
            onChange={handleFieldChange('min_scale')}
            value={value?.min_scale ?? ''}
            placeholder="1"
          />
          <TextField
            label="Maximum scale"
            type="number"
            onChange={handleFieldChange('max_scale')}
            value={value?.max_scale ?? ''}
            placeholder="1"
          />
        </div>
      </div>

      <hr className="col-start-1 col-end-4 border-tertiary" />

      <div className="flex flex-col items-start">
        <strong className="font-semibold">Queue Resources (optional)</strong>
        <p className="text-sm text-tertiary">
          Leave blank to inherit from the values above.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Queue CPU per container"
            type="number"
            onChange={handleFieldChange('queue_cpu')}
            value={value?.queue_cpu ?? ''}
          />
          <TextField
            label="Queue memory (MB) per container"
            type="number"
            onChange={handleFieldChange('queue_memory_mb')}
            value={value?.queue_memory_mb ?? ''}
          />
          <TextField
            label="Queue minimum scale"
            type="number"
            onChange={handleFieldChange('queue_min_scale')}
            value={value?.queue_min_scale ?? ''}
          />
          <TextField
            label="Queue maximum scale"
            type="number"
            onChange={handleFieldChange('queue_max_scale')}
            value={value?.queue_max_scale ?? ''}
          />
        </div>
      </div>
    </>
  );
}
